import { Check, Sparkles } from 'lucide-react';
import { getNestedValue } from '../utils';
import { Button } from '@/components/ui-kit/button';
import { useMediaQuery } from '@/hooks/use-media-query';

/**
 * AI Suggestion Indicator - unified component for showing AI-suggested standard values
 *
 * States:
 * 1. Loading - waiting for enrichment context (shows subtle AI badge)
 * 2. Applied - value matches suggestion (shows green checkmark)
 * 3. Available - suggestion ready, field empty (shows "Use standard: [value]" button)
 * 4. Hidden - user is typing their own value (no UI shown, don't interfere)
 */
export default function AISuggestionIndicator({
  suggestionKey,
  enrichmentContext,
  currentValue,
  onApply,
  variant = 'label', // "label" for header position, "inline" for inside input
}: {
  suggestionKey: string;
  enrichmentContext?: Record<string, unknown>;
  currentValue: unknown;
  onApply: (value: unknown) => void;
  variant?: 'label' | 'inline';
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  if (!suggestionKey) return null;

  // State 1: Loading - waiting for enrichment context
  if (!enrichmentContext || Object.keys(enrichmentContext).length === 0) {
    // Only show loading state in label variant to avoid clutter
    if (variant === 'inline') return null;
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full 
          bg-[hsl(var(--globe-grey))]/10 text-globe-grey"
        title="AI suggestion will appear after completing the previous step"
      >
        <Sparkles className="h-3 w-3 opacity-50" />
        <span className="opacity-70">AI</span>
      </span>
    );
  }

  const suggestedValue = getNestedValue(enrichmentContext, suggestionKey);

  // No suggestion available
  if (suggestedValue === undefined || suggestedValue === null) {
    return null;
  }

  const suggestedStr = String(suggestedValue);
  const currentStr =
    currentValue !== undefined && currentValue !== null ? String(currentValue) : '';

  // State 2: Applied - value matches suggestion
  if (suggestedStr === currentStr) {
    // Only show in label variant
    if (variant === 'inline') return null;
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full 
          bg-emerald-50 text-emerald-600 border border-emerald-200"
      >
        <Check className="h-3 w-3" />
        <span>Standard applied</span>
      </span>
    );
  }

  // State 4: Hidden - user has typed their own value (different from suggestion and not empty)
  // Don't show suggestion UI when user is entering custom value to avoid confusion
  const hasCustomValue = currentStr.length > 0 && currentStr !== suggestedStr;
  if (hasCustomValue) {
    return null;
  }

  // State 3: Available - suggestion ready, show apply button
  // Only show in one location (inline variant takes priority when field is empty)
  if (variant === 'label') {
    // For label position, only show if this is being used alone (for backwards compat)
    return null;
  }

  // Truncate long values for display (shorter on mobile)
  const displayValue =
    suggestedStr.length > 20 ? suggestedStr.substring(0, 20) + '...' : suggestedStr;
  const displayValueMobile =
    suggestedStr.length > 15 ? suggestedStr.substring(0, 15) + '...' : suggestedStr;

  return (
    <Button
      size="sm"
      variant="primaryOutline"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onApply(suggestedValue);
      }}
      className="w-full md:w-auto bg-selise-blue-500/10 hover:bg-selise-blue-500/20"
      title={`Apply standard value: ${suggestedStr}`}
    >
      <Sparkles className="h-4 w-4 sm:h-3 sm:w-3 shrink-0" />
      <span className="whitespace-nowrap">
        <span className="hidden sm:inline">Use standard: </span>
        <span className="sm:hidden">Standard: </span>
        <span className="font-semibold">
          <span className="hidden sm:inline">{displayValue}</span>
          <span className="sm:hidden">{displayValueMobile}</span>
        </span>
      </span>
    </Button>
  );
}
